import React, { useEffect, useState } from 'react'
import Modal from '@berlitzplatforms/micro.ui.modal'
import { IGetCalendarEvent } from '@interfaces/CalendarEvents'
import { DOWNLOAD_CAL_MODAL_PROPS } from './constants'
import ModalTransactional from '@berlitzplatforms/micro.modal.transactional'
import { ModalDownloadCalWrapper, ModalIconWrapper, StyledH2 } from './style'
import Button from '@berlitzplatforms/micro.ui.button'
import { FormattedMessage, useIntl } from 'react-intl'
import Spacer from '@berlitz/spacer'
import { LoadingContent } from '@components/Modal/LoadingModal'
import { PUT_STUDENTS } from '@mutations/index'
import { useMutation } from '@apollo/react-hooks'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import useCalendarEvents from '@hooks/useCalendarEvents'
import { createEvents } from 'ics'
import { saveAs } from 'file-saver'
import { useRouter } from 'next/router'
import { isiOS } from '@utils/browser'
import { StoreProps } from '@interfaces/StoreState'
import { useSelector } from 'react-redux'

interface IDownloadCalendarModal {
  onClose?: () => void
  open?: boolean
  studentCalendar: IGetCalendarEvent[]
  hasChanges?: boolean
  downloadButtons: string[]
  onDownloaded: (downloaded: boolean) => void
  downloaded: boolean
}

const DownloadCalendarModal: React.FC<IDownloadCalendarModal & WithUserTimeProps> = ({
  onClose = () => {},
  open = false,
  studentCalendar = [],
  hasChanges = false,
  downloadButtons = [],
  getDateWithUserTimezone,
  onDownloaded = () => {},
  downloaded = false,
}) => {
  const router = useRouter()
  const intl = useIntl()
  const { updateStudentPayload, generateICS } = useCalendarEvents(studentCalendar)
  const modalProps = hasChanges ? DOWNLOAD_CAL_MODAL_PROPS.hasChanges : DOWNLOAD_CAL_MODAL_PROPS.default
  const [generating, setGenerating] = useState(false)
  const [hasError, setHasError] = useState(false)
  // Add UserId from Redux store
  const { UserId } = useSelector(({ userProfile }: StoreProps) => ({
    UserId: userProfile?.info?.SFId,
  }))
  const [updateStudent, { loading, error }] = useMutation(PUT_STUDENTS, {
    onCompleted: () => {
      createEvents(generateICS(), (error, value) => {
        const blob = new Blob([value], { type: 'text/plain;charset=utf-8' })
        saveAs(blob, `MyBerlitzCalendar-${getDateWithUserTimezone().format('YYYYMMDDHHmmss')}.ics`)

        if (!error) {
          onDownloaded(true)
        } else {
          setHasError(true)
        }
      })
    },
  })

  useEffect(() => {
    setGenerating(loading)
  }, [loading])

  useEffect(() => {
    setHasError(!!error)
  }, [error])

  return (
    <Modal
      onClose={() => {
        onClose()

        let timeout = setTimeout(() => {
          setGenerating(false)

          return () => clearTimeout(timeout)
        }, 300)
      }}
      open={open}
    >
      {generating ? (
        <LoadingContent title={intl.formatMessage({ id: 'Downloading calendar...' })} />
      ) : (
        <ModalDownloadCalWrapper className={downloaded || hasError ? 'info' : ''}>
          {downloaded || hasError ? (
            <>
              <Spacer size="lg" />

              {downloaded ? (
                <ModalTransactional
                  status="success"
                  title={intl.formatMessage({
                    id: 'Download complete',
                    defaultMessage: 'Download complete',
                  })}
                  description={
                    <>
                      {isiOS() ? (
                        <>
                          <StyledH2>
                            <FormattedMessage id="iOS Sync Support" defaultMessage="iOS Sync Support" />
                          </StyledH2>
                          <Spacer size="md" />
                          <div>
                            <FormattedMessage
                              id="iOS does not currently support calendar import from a downloaded file. Please visit our FAQs for support in adding the calendar"
                              defaultMessage="iOS does not currently support calendar import from a downloaded file. Please visit our FAQs for support in adding the calendar"
                            />
                          </div>
                          <Spacer size="md" />
                        </>
                      ) : (
                        <></>
                      )}
                      <Button
                        onClick={() => {
                          router.push('/support/?id=troubleshooting')
                        }}
                      >
                        <FormattedMessage id="Help Center" defaultMessage="Help Center" />
                      </Button>
                    </>
                  }
                />
              ) : (
                <ModalTransactional
                  status="warning"
                  title={intl.formatMessage({
                    id: 'Oh no! Something went wrong',
                    defaultMessage: 'Oh no! Something went wrong',
                    description: 'Please try again later or contact the Support team.',
                  })}
                  description={intl.formatMessage({
                    id: 'Please try again later or contact the Support team.',
                    defaultMessage: 'Please try again later or contact the Support team.',
                  })}
                />
              )}
            </>
          ) : (
            <ModalTransactional
              image={<ModalIconWrapper>{modalProps.icon}</ModalIconWrapper>}
              title={intl.formatMessage({
                id: modalProps.header,
                defaultMessage: modalProps.header,
              })}
              description={intl.formatMessage({
                id: modalProps.desc,
                defaultMessage: modalProps.desc,
              })}
            />
          )}

          {downloaded || hasError ? null : (
            <>
              {downloadButtons?.map((label) => (
                <Button
                  key={label}
                  outline
                  ghostInvert
                  onClick={() => {
                    updateStudent({
                      variables: {
                        ...updateStudentPayload(getDateWithUserTimezone),
                        requestType: 'DOWNLOAD_CALENDAR',
                        UserId: UserId, 
                      },
                      awaitRefetchQueries: true,
                      refetchQueries: ['GetCalendarEvents'],
                    })
                  }}
                >
                  {intl.formatMessage({ id: label, defaultMessage: label })}
                </Button>
              ))}

              <Spacer size="md" />
            </>
          )}
        </ModalDownloadCalWrapper>
      )}
    </Modal>
  )
}

export default WithUserTimeHOC(DownloadCalendarModal)
